import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsExclamationCircle } from 'react-icons/bs';
import { getApiUrl } from '../../config/api';
import VideoPlayer from '../../pages/locationPage/videoplayer';
import api from '../../utils/api';
import FileViewer from 'react-file-viewer'; 
import "./contentbox.css"
import ReactMarkdown from 'react-markdown';

type TContentBoxProps = {
    src?: string;
    type?: string;
    thumbnail?: string;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    bigPlayButton?: boolean;
    controls?: boolean;
    className?: string;
    getWarning?: (container: "empty" | "error" | "video" | "image" | "iframe" | "youtube" | "document", src: string, type: string)=>string | null;
}

type TLoadedContent = {
    src: string;
    mimeType: string;
    error: boolean;
}
const NO_CONTENT: TLoadedContent = { src: "", mimeType: "", error: false}

export default function ContentBox(props: TContentBoxProps) {
    const [loadedContent, setLoadedContent] = useState(NO_CONTENT)
    const [error, setError] = useState(false);
    const [loaded, setLoaded] = useState(false);

     // New state for markdown content, that needs loading
    const [mdContent, setMdContent] = useState<string | null>(null);
    const [mdError, setMdError] = useState(false);

    // Whenever we detect a markdown file, fetch its text
    useEffect(() => {
        if (
        loadedContent.mimeType === 'text/markdown' ||
        loadedContent.mimeType === 'text/x-markdown'
        ) {
        setMdError(false);
        fetch(loadedContent.src)
            .then(res => {
            if (!res.ok) throw new Error('Network error');
            return res.text();
            })
            .then(text => setMdContent(text))
            .catch(err => {
            console.error('Markdown fetch failed', err);
            setMdError(true);
            });
        }
    }, [loadedContent]);


    //effect to load the content
    useEffect(() => {

        //clear state
        setLoadedContent(NO_CONTENT)

        //check if mime type supplied
        if(props.src && props.src != "") {
            if(props.type) {
                //got mime type, so can just store it
                setLoadedContent({
                    src: props.src,
                    mimeType: props.type.toLowerCase(),
                    error: false
                })
            } else {
                //no mime type so have to load it
                const loading_url = props.src;
                api.get<any>({ url: `/v2/head?url=${encodeURIComponent(loading_url)}`, hideErrors: true })
                .then((res) => {
                    if(loading_url === props.src) { //ensures url hasn't changed since request
                        if(res.headers["content-type"]) {
                            setLoadedContent({
                                src: props.src,
                                mimeType: res.headers["content-type"].toLowerCase(),
                                error: false
                            })
                        } else {
                            setError(true);
                        }
                    }
                })
                .catch((err)=>{
                    if(loading_url === props.src) { //ensures url hasn't changed since request
                        setLoadedContent({
                            src: props.src,
                            mimeType: "",
                            error: true
                        })
                    }
                })
            }            
        }
    }, [props.src, props.type])

    /*
    useEffect(() => {
        console.log("Loaded content changed");
        console.log(loadedContent);
    }, [loadedContent])
    */

    const onImageLoaded = () => {
        setLoaded(true);
    }

    const getContainerType = () => {
        const {src,mimeType} = loadedContent;
        if(mimeType == "") {
            return "empty";
        } else if(mimeType.startsWith("image/")) {
            return "image";
        } else if((mimeType.startsWith("video/") || mimeType.startsWith("application/x-mpegurl") || mimeType.startsWith("application/vnd.apple.mpegurl"))) {
            return "video";
        } else if(mimeType.startsWith("text/html")) {
            return src.includes("youtube.com") ? "youtube" : "iframe";
        } else if(  mimeType == "application/pdf" ||
                    mimeType == "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                    mimeType == "text/markdown" || mimeType == "text/x-markdown" ) {
            return "document";
        } else {
            return "error";
        }       
    }

    const emptyContainer = () => (
        <div></div>
    )

    const imageContainer = () => (
        <img 
            referrerPolicy="no-referrer" 
            src={loadedContent.src} 
            onLoad={onImageLoaded} 
        />
    )

    const videoContainer = () => (
        <VideoPlayer 
            autoplay={props.autoplay} 
            muted={props.muted} 
            loop={props.loop} 
            bigPlayButton={props.bigPlayButton} 
            controls={props.controls}
            sources={[{ src: loadedContent.src, type: loadedContent.mimeType }]}
        />
    )

    const iFrameContainer = () => (
        <iframe src={loadedContent.src} scrolling="no"/>
    )

    const youtubeContainer = () => {
        const vid_id = loadedContent.src.match(/.*v\=(.*)/)
        return <iframe src={`${getApiUrl()}/v2/public/viewers/youtube/${vid_id![1]}`} scrolling="no" />
    }

    const documentContainer = () => {
        let fileType = "docx";
        if(loadedContent.mimeType == "application/pdf") {
            fileType = "pdf";
        } else if(loadedContent.mimeType == "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
            fileType = "docx";
        } else if(loadedContent.mimeType == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            fileType = "xlsx";
        } else if(loadedContent.mimeType == "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
            fileType = "pptx";
        } else if(loadedContent.mimeType == "text/markdown" || loadedContent.mimeType == "text/x-markdown") {
            fileType = "md";
        }
        
        // If it’s markdown, render our Markdown viewer
        if (fileType === 'md') {
            if (mdError) {
            return <div>Couldn’t load Markdown preview</div>;
            }
            if (mdContent === null) {
            return <div>Loading…</div>;
            }
            return (
            <div
                style={{
                flex: '1 1 auto',
                overflowY: 'auto',
                padding: '1rem',
                color: '#000',
                backgroundColor: '#fff',
                }}
            >
                <ReactMarkdown>{mdContent}</ReactMarkdown>
            </div>
            );
        }
    
        // Otherwise fall back to FileViewer
          return (
            <div
                style={{
                color: "#000",
                opacity: 1,
                filter: "none",
                /* make this fill the modal-body but never scroll itself */
                display: "flex",
                flex: "1 1 auto",
                justifyContent: "center",
                overflow: "hidden",
                }}
            >
                <FileViewer
                fileType={fileType}
                filePath={loadedContent.src}
                onError={(e) => {
                    console.error(e);
                    setError(true);
                }}
                />
            </div>
            );
    }

    const errorContainer = () => (
        <div></div>
    )

    const containers = {
        "empty": emptyContainer,
        "image": imageContainer,
        "video": videoContainer,
        "iframe": iFrameContainer,
        "youtube": youtubeContainer,
        "document": documentContainer,
        "error": errorContainer
    }

    const container = () => containers[getContainerType()]();

    const warningText = () => {
        return props.getWarning && props.getWarning(getContainerType(), loadedContent.src, loadedContent.mimeType);
    }

    const warningTooltip = (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
            {warningText()}
        </Tooltip>
    );

    const warningOverlay = () => (
        warningText() && (<OverlayTrigger placement="bottom-end" delay={{ show: 0, hide: 400 }} overlay={warningTooltip}>
            <div className="mv-content-box-warning">
                <BsExclamationCircle size="30px"/>
            </div>
        </OverlayTrigger>)
    )

    // return (
    //     <div className={"mv-content-box " + props.className || ""}> 
    //         {container()}
    //         {warningOverlay()}
    //     </div>
    // )

        return (
              <>
                {/* 1) Scoped CSS-override for just this dialog */}
                <style>{`
                  /* flex-column the modal so header stays out of the body’s scroll */
                  .drive-browser-preview-dialog .modal-content {
                    display: flex !important;
                    flex-direction: column !important;
                    height: 100% !important;
                  }
                  /* stick the header to the top */
                  .drive-browser-preview-dialog .modal-header {
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                    background-color: #000 !important;
                    flex: 0 0 auto !important;            /* no shrinking */
                  }
                  /* make *only* the body scroll */
                  .drive-browser-preview-dialog .modal-body {
                    flex: 1 1 auto !important;
                    overflow-y: auto !important;
                  }
                  /* kill any nested scrolling inside our ContentBox */
                  .drive-browser-preview-dialog .modal-body > .mv-content-box {
                    overflow: visible !important;
                  }
                `}</style>
        
                {/* 2) Your normal content box */}
                <div className={`mv-content-box ${props.className || ""}`}>
                  {container()}
                  {warningOverlay()}
                </div>
              </>
            );    

}

